/* eslint-disable @typescript-eslint/no-var-requires */
import { find, isEmpty, isUndefined, result } from 'lodash';
import numeral from 'numeral';
import React, { Component } from 'react';
import Autosuggest from 'react-autosuggest';
import { debounce, memoize } from 'lodash';
import styles from './NewFundSearch.scss';

import config from '../../../config/config';
import { ADD_FUND_MODAL } from '../../../redux/modules/modals/modals.module';
import { PRODUCT_TYPE_LABEL_MAP } from '../../../settings/settings';
import Button from '../../Shared/Button/Button';
import If from '../../Shared/If/If';
import Loader from '../../Shared/Loader/Loader';
import { scrollElementIntoFullView } from '../../../utils/dom-utils';
import { agreementTypeToDisplay } from '../../../utils/funds/funds-utility';
import {
  isCollegeAnything,
  isCollegeContribute,
  isCollegeExchange,
  isExchange,
  getFundLimit,
} from '../../../utils/transactions-util';
import { coalesce, hasValue } from '../../../utils/utils';
import {
  checkMFBuyOrExchangeFlow,
  getHouseHoldInformation,
} from '../../../utils/funds/shared/shared-fund-notes-utility';
import NewFundSearchSuggestionNotes from './NewFundSearchSuggestionNotes';
import NewFundSearchSelectedFundNotes from './NewFundSearchSelectedFundNotes';
import { v4 as uuidv4 } from 'uuid';

const registrationTypeURLMap = {
  Maryland529: 'https://maryland529.com/Investment-Plan/Investment-Options',
  Alaska529: 'https://www.alaska529plan.com/investment-options.html',
  National529:
    'https://www.troweprice.com/personal-investing/troweprice-529/investment-options.html',
  MutualFund: `${config.url}/personal-investing/tools/fund-research/`,
  defaultURL: `${config.url}/personal-investing/tools/fund-research/`,
};

export class NewFundSearch extends Component {
  constructor(props) {
    super(props);
    this.state = {
      started: true,
      focused: false,
      value: '',
      suggestions: [],
      ticker: '',
      selectedFund: {},
      addNewFundDisable: true,
      newAgreementId: uuidv4(), // generating uniqueId for the new added fund as agreementId
    };
    this.addFundButton = React.createRef();
    this.onFocus = this.onFocus.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.onSuggestionsFetchRequested = debounce(
      this.onSuggestionsFetchRequested.bind(this),
      300
    );
  }

  componentDidMount() {
    const {
      actions: { getTRPFunds },
      addFunds,
      dispatchPageLoad,
    } = this.props;
    if (isEmpty(addFunds)) {
      getTRPFunds();
    }
    dispatchPageLoad('New Fund Search');
  }

  componentWillUnmount() {
    const { newAgreementId } = this.state;
    setTimeout(
      () => scrollElementIntoFullView(`fund-agreement-${newAgreementId}`),
      150
    );
  }

  // TODO: Replace with <EmphasizeSubtext /> but need to extract applyTrpRephraseRules from EmphasizeSubtext first
  getSuggestion = memoize((suggestion, q) => {
    const suggestionText = `${suggestion}`;
    const i = suggestionText.toLowerCase().indexOf(q.toLowerCase());
    if (i === -1) {
      return suggestion;
    }
    const pre = suggestionText.substring(0, i) || null;
    const queryText = suggestionText.substring(i, i + q.length);
    const post = suggestionText.substring(i + q.length) || null;
    return (
      <span>
        {pre && <span>{pre}</span>}
        <span className="new-fund-search-suggestion-match">{queryText}</span>
        {post && <span>{post}</span>}
      </span>
    );
  });

  /**
   * @param {AllFund} fund
   * @param {string} query
   * @returns {object}
   */
  renderSuggestion = (fund, { query }) => {
    const {
      modalProps: { showSymbol, fundType },
    } = this.props;
    /* Checking this to show new Modal design */
    const isMFBuyOrExchange = this.isMFBuyOrExchangeFlow();
    return (
      <div
        className={
          fund.disabled
            ? 'new-fund-search-fund-disable'
            : 'new-fund-search-fund-enable'
        }
      >
        {isMFBuyOrExchange ? (
          <div className="new-fund-search-suggestion-name-scs">
            {this.getSuggestion(fund.name, query)}
            {showSymbol && (
              <span className="new-fund-search-suggestion-ticker-scs">
                ({this.getSuggestion(fund.ticker, query)})
              </span>
            )}
          </div>
        ) : (
          <>
            {showSymbol && (
              <div className="new-fund-search-suggestion-ticker">
                {this.getSuggestion(fund.ticker, query)}
              </div>
            )}
            <div>{this.getSuggestion(fund.name, query)}</div>{' '}
          </>
        )}
        <NewFundSearchSuggestionNotes
          fund={fund}
          fundType={fundType}
          scsTier={this.getHouseHoldTier()}
        />
      </div>
    );
  };

  shouldRenderSuggestions = () => {
    return this.state.focused;
  };

  getSuggestions = (value) => {
    const { addFunds } = this.props;
    if (addFunds.length === 0) {
      return [];
    }

    const inputValue = coalesce(value, '').trim().toLowerCase();
    const inputLength = inputValue.length;
    const regex = new RegExp('\\b' + value, 'i');
    const isSCSTier = this.getHouseHoldTier();
    const suggestedFunds = (
      inputLength === 0
        ? addFunds
        : addFunds.filter(
            (fund) => regex.test(fund.name) || regex.test(fund.ticker)
          )
    ).filter((fund) => {
      /* Showing iClass and Closed funds only to the tier 3 and above clients */

      if (isSCSTier === 'closed' && fund.iClass) {
        return false;
      }
      const isMutualFundBuyOrExchange = this.isMFBuyOrExchangeFlow();
      if (
        !isSCSTier &&
        isMutualFundBuyOrExchange &&
        (!fund.openToNewInvestors || fund.iClass)
      ) {
        return false;
      }
      return true;
    });

    return suggestedFunds;
  };

  getSuggestionValue = (suggestion) => {
    if (suggestion.disabled) {
      this.setState({
        value: '',
        selectedFund: {},
        ticker: '',
      });
    } else {
      this.setState({ ticker: suggestion.ticker, selectedFund: suggestion });
    }
    return suggestion.name;
  };

  onSuggestionSelected = (e, obj) => {
    if (obj.suggestion.disabled) {
      this.setState({
        value: '',
        selectedFund: {},
        ticker: '',
      });
    }
  };

  onFocus = () => {
    this.setState({
      focused: true,
      suggestions: this.getSuggestions(''),
    });
  };

  onBlur = () => {
    this.setState({ focused: false });
  };

  onChange = (event, obj) => {
    if (!isUndefined(obj.newValue)) {
      const newValue = obj.newValue;
      if (newValue.length === 0) {
        this.setState({ ticker: '', selectedFund: {} });
      }

      this.setState({
        value: newValue,
      });
    }
  };

  onSuggestionsFetchRequested = ({ value }) => {
    this.setState({
      suggestions: this.getSuggestions(value),
    });
  };

  onSuggestionsClearRequested = () => {
    this.setState({
      suggestions: [],
    });
  };

  renderInputComponent = (inputProps) => {
    const {
      modalProps: { showSymbol },
    } = this.props;
    /* Checking this to show new Modal design */
    const isMFBuyOrExchange = this.isMFBuyOrExchangeFlow();
    return (
      <div>
        <input
          {...inputProps}
          className={`e2e-new-fund-search-input ${coalesce(
            inputProps.className,
            ''
          )}`}
        />
        {showSymbol && (
          <div
            className={`new-fund-search-search-box-ticker ${
              isMFBuyOrExchange && 'new-fund-search-search-box-ticker-scs'
            }`}
          >
            {this.state.ticker}
          </div>
        )}
      </div>
    );
  };

  onAddNewFundClick = () => {
    const {
      userRegistrationTypeInfo: { selectedRegistrationType },
      actions: { addToNewFund, closeModal, selectFund },
      availableFundList,
    } = this.props;
    const { selectedFund } = this.state;
    const { newAgreementId } = this.state;

    const newFund = {
      ...selectedFund,
      ...selectedRegistrationType,
      newFund: true,
      agreementID: newAgreementId,
      displayClassification: selectedRegistrationType.classification,
      fundName: selectedFund.name,
      selected: true,
      bankAssociated: true,
    };

    const selectedRegistrationProductType =
      selectedRegistrationType &&
      (selectedRegistrationType.productType === 'MUTUAL_FUND' ||
        selectedRegistrationType.productType === 'ManagedAccount')
        ? 'MutualFund'
        : selectedRegistrationType.productType;

    const nextFund = find(
      availableFundList,
      (fund) =>
        fund.displayClassification === newFund.displayClassification &&
        fund.agreementType === newFund.agreementType
    );
    const newlyAddedFund = find(
      availableFundList,
      (fund) => fund.formattedEntityNames === newFund.formattedEntityNames
    ); // This is the problem maybe
    newFund.productType = selectedRegistrationProductType || 'MutualFund';

    // added Undefined check because the BPID for non minorIRA returns '0'.
    // if the value is 0 it takes as false and returns null. which will not return any IRA Limits.
    newFund.BPID =
      newlyAddedFund && !isUndefined(newlyAddedFund['BPID'])
        ? newlyAddedFund['BPID']
        : null;
    newFund.ownerName = newlyAddedFund && newlyAddedFund.ownerName;
    newFund.isMinorIRA = newlyAddedFund && newlyAddedFund.isMinorIRA;
    newFund.sortRank = nextFund ? nextFund.sortRank - 1 : Number.MAX_VALUE;
    addToNewFund(newFund); // Note: In case of exchange this is actually addExchangeToNewFund
    selectFund(newAgreementId, newFund, newFund.BPID); // Note: In case of exchange this is actually selectExchangeToFund
    closeModal(ADD_FUND_MODAL);
  };

  shouldButtonDisable = () => {
    return this.state.ticker.length === 0;
  };

  getButtonStyle = () => {
    return this.shouldButtonDisable()
      ? 'btn btn-trp-primary btn-disabled'
      : 'btn btn-trp-primary';
  };

  onBackClick = () => {
    const { setFundProgress } = this.props;
    setFundProgress('REGISTRATION');
  };

  onEnterKeyDown = (e) => {
    if (e.keyCode !== 13 || this.state.ticker === '') {
      return;
    }

    const { addFunds } = this.props;
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;
    const fundName = result(
      find(addFunds, (obj) => {
        return obj.ticker === that.state.ticker;
      }),
      'name'
    );
    if (e.target.value !== fundName) {
      this.setState({
        value: '',
        selectedFund: {},
        ticker: '',
      });
    }

    // Need to delay the focus call or else the enter press will get passed to the button
    // and it'll trigger a click; funky.
    setTimeout(() => this.addFundButton.current.focus(), 10);
  };

  isMFBuyOrExchangeFlow = () => {
    return checkMFBuyOrExchangeFlow();
  };

  getHouseHoldTier = () => {
    const { userRegistrationTypeInfo } = this.props;
    const { selectedRegistrationType } = userRegistrationTypeInfo;
    return getHouseHoldInformation(selectedRegistrationType);
  };

  getClosedFunds(addFunds) {
    return addFunds?.filter((fund) => !fund.openToNewInvestors && !fund.iClass);
  }

  getIclassFunds(addFunds) {
    return addFunds?.filter((fund) => fund.iClass && !fund.openToNewInvestors);
  }

  getCurrentFunds(
    isPersonalOrEnhancedTier,
    iClassFunds,
    isSelectTier,
    closedFunds
  ) {
    if (isPersonalOrEnhancedTier) {
      return iClassFunds;
    } else if (isSelectTier) {
      return closedFunds;
    }
    return '';
  }

  getRegistrationName(selectedRegistrationProductType, agreementType) {
    return isCollegeAnything()
      ? PRODUCT_TYPE_LABEL_MAP[selectedRegistrationProductType]
      : agreementTypeToDisplay(agreementType);
  }

  checkIfExchange() {
    return isExchange() || isCollegeExchange();
  }

  render() {
    const {
      exchangeToNewLoading,
      modalProps: { fundType, showIClass, showProspectus, showSymbol },
      addFunds,
      userRegistrationTypeInfo,
    } = this.props;
    const { selectedRegistrationType } = userRegistrationTypeInfo;

    const {
      agreementType,
      formattedEntityNames,
      productType: selectedRegistrationProductType, // Renaming `productType` to give it more meaning when used
    } = selectedRegistrationType;

    const { focused, selectedFund, suggestions, value } = this.state;
    const closedFunds = this.getClosedFunds(addFunds);
    const iClassFunds = this.getIclassFunds(addFunds);

    const isMutualFundBuyOrExchange = this.isMFBuyOrExchangeFlow();
    const isSCSTier = this.getHouseHoldTier();
    const isPersonalOrEnhancedTier = isSCSTier === 'iClass';
    const isSelectTier = isSCSTier === 'closed';
    const currentFunds = this.getCurrentFunds(
      isPersonalOrEnhancedTier,
      iClassFunds,
      isSelectTier,
      closedFunds
    );
    const loaderStyle = {
      height: '600px',
      paddingTop: '200px',
      textAlign: 'center',
    };
    const registrationName = this.getRegistrationName(
      selectedRegistrationProductType,
      agreementType
    );
    // TODO genericize this loading indicator for all flows
    if (exchangeToNewLoading) {
      return (
        <Loader
          contextStyle={loaderStyle}
          color="rgb(118, 118, 118)"
          isVisible
        />
      );
    }

    const autoSuggestInput = (
      <Autosuggest
        alwaysRenderSuggestions={focused}
        suggestions={suggestions}
        focusInputOnSuggestionClick={false}
        onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
        onSuggestionsClearRequested={this.onSuggestionsClearRequested}
        getSuggestionValue={this.getSuggestionValue}
        renderSuggestion={this.renderSuggestion}
        inputProps={{
          'aria-label': `Lookup new ${fundType} by name ${
            showSymbol ? 'or symbol' : ''
          }`,
          placeholder: `Enter ${fundType} name ${
            showSymbol ? 'or symbol' : ''
          }`,
          value,
          onChange: this.onChange,
          size: '70',
          onFocus: this.onFocus,
          onBlur: this.onBlur,
          onKeyDown: this.onEnterKeyDown,
        }}
        shouldRenderSuggestions={this.shouldRenderSuggestions}
        renderInputComponent={this.renderInputComponent}
        theme={styles}
        onSuggestionSelected={this.onSuggestionSelected}
      />
    );

    return (
      <>
        {!isMutualFundBuyOrExchange ? (
          <div className="new-fund-search">
            <div className="e2e-new-fund-title new-fund-search-title">
              <span className="hidden-xs">
                Search for the {fundType} you’d like to add
              </span>
              <span className="visible-xs">Search for a {fundType}</span>
            </div>
            <div className="new-fund-search-registration-type-line overflow-hid">
              Add a new {fundType} to
              <span className="new-fund-search-registration-type">
                {' '}
                <span className="e2e-new-fund-agreement-type">
                  {registrationName}
                </span>{' '}
                &mdash;{' '}
                <span className="e2e-new-fund-user-names blocked_data">
                  {formattedEntityNames}
                </span>
                :
              </span>
            </div>
            <div className="new-fund-search-autosuggestion-container e2e-new-fund-search-input-container">
              {autoSuggestInput}
              <If
                test={
                  isEmpty(selectedFund) ||
                  !(selectedFund.summit || selectedFund.ticker === 'TRLAX')
                }
              >
                <div className="new-fund-search-min-contribution e2e-new-fund-min-contribution-generic">
                  Minimum investment amount is{' '}
                  {numeral(
                    getFundLimit({ selectedFund, userRegistrationTypeInfo })
                  ).format('$0,0')}
                  , unless otherwise noted.
                </div>
              </If>
              {hasValue(selectedFund) && (
                <div className="new-fund-search-chosen-fund-container e2e-new-fund-chosen-container">
                  <div className="new-fund-search-chosen-fund">
                    CHOSEN {fundType.toUpperCase()}
                  </div>
                  <div>
                    <span className="new-fund-search-chosen-fund-name">
                      {selectedFund.name}
                    </span>
                    {showSymbol && (
                      <span className="new-fund-search-chosen-fund-ticker">
                        {selectedFund.ticker}
                      </span>
                    )}
                  </div>
                  <NewFundSearchSelectedFundNotes
                    selectedFund={selectedFund}
                    showProspectus={showProspectus}
                    selectedRegistrationProductType={
                      selectedRegistrationProductType
                    }
                    scsProgram={false}
                  />
                </div>
              )}
            </div>

            <If test={showIClass && isEmpty(selectedFund)}>
              <div className="new-fund-search-iclass-note e2e-new-fund-iclass-note">
                To invest in I-Class call 1-877-218-5162 ($1,000,000 minimum
                starting contribution)
              </div>
            </If>
            <div
              className={`new-fund-search-buttons-container ${
                this.checkIfExchange() ? 'new-fund-search-hide-back-button' : ''
              }`}
            >
              {!this.checkIfExchange() && (
                <Button
                  id="fund-search-back-btn"
                  btnStyle="btn btn-trp-secondary"
                  onClick={this.onBackClick}
                >
                  Back
                </Button>
              )}
              <Button
                id="fund-search-back-submit"
                btnStyle={this.getButtonStyle()}
                className="e2e-new-fund-add-button"
                disabled={this.shouldButtonDisable()}
                onClick={this.onAddNewFundClick}
                ref={this.addFundButton}
              >
                Add {fundType}
              </Button>
            </div>
            <div className="new-fund-search-off-ramp e2e-off-ramp-text">
              <a
                className="e2e-off-ramp-text-link college-branded-link"
                rel="noopener noreferrer"
                href={
                  registrationTypeURLMap[selectedRegistrationProductType] ||
                  registrationTypeURLMap.defaultURL
                }
                target="_blank"
              >
                Browse all {fundType}s
              </a>
              {isCollegeContribute()
                ? ' to find the right choice for your educational savings.'
                : ` to find the right ${fundType} for your investment strategy.`}
            </div>
          </div>
        ) : (
          <div>
            <div className="new-fund-search scs-newfund-search">
              <div className="e2e-new-fund-title new-fund-search-title-scs">
                <span>Choose the {fundType} you’d like to add</span>
              </div>
              <div className="new-fund-search-registration-type-line">
                <span className="e2e-new-fund-user-names-scs blocked_data">
                  Search Funds
                </span>
              </div>
              <div className="new-fund-search-autosuggestion-container e2e-new-fund-search-input-container scs-main-container">
                {autoSuggestInput}
                <If
                  test={
                    isEmpty(selectedFund) ||
                    !(selectedFund.summit || selectedFund.ticker === 'TRLAX')
                  }
                >
                  <div className="new-fund-search-min-contribution-scs e2e-new-fund-min-contribution-generic">
                    Minimum investment amount is{' '}
                    {numeral(
                      getFundLimit({ selectedFund, userRegistrationTypeInfo })
                    ).format('$0,0')}
                    , unless otherwise noted.
                  </div>
                </If>

                <div>
                  {isSelectTier && (
                    <div>
                      <h3 className="closed-fund-title">
                        You have access to invest in closed funds:
                      </h3>
                      <div className="closed-funds-subheader">
                        Each fund requires a minimum investment of{' '}
                        {numeral(
                          getFundLimit({
                            selectedFund,
                            userRegistrationTypeInfo,
                          })
                        ).format('$0,0')}
                        .
                      </div>
                    </div>
                  )}
                  {isPersonalOrEnhancedTier && (
                    <div>
                      <h3 className="closed-fund-title">
                        You have access to invest in these T. Rowe Price funds:
                      </h3>
                      <div className="">
                        <h4 className="iclass-fund-title">
                          Lower-cost I Class Shares
                        </h4>
                        <div className="closed-funds-subheader">
                          You are eligible to invest in funds with lower-cost I
                          Class shares ($50,000 fund minimum, per fund).
                        </div>
                      </div>
                      <div className="">
                        <h4 className="iclass-fund-title">
                          Closed Funds with I Class Shares
                        </h4>
                        <div className="closed-funds-subheader">
                          You have the opportunity to invest in these T. Rowe
                          Price closed funds.
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="grid-wrapper">
                    {currentFunds &&
                      currentFunds.map((fund) => (
                        <>
                          {!fund.disabled && (
                            <div className="box">
                              <div className="header">
                                {fund.name} - {fund.ticker}
                              </div>
                              <Button
                                role="button"
                                id={`new-fund-box-${fund.ticker}`}
                                disabled={false}
                                className="new-fund-button"
                                onClick={() => {
                                  this.setState({
                                    value: this.getSuggestionValue(fund),
                                  });
                                }}
                              >
                                Add Fund
                              </Button>
                            </div>
                          )}
                        </>
                      ))}
                  </div>
                </div>
                <div className="space"></div>

                <div className="new-fund-mutual-fund-message">
                  Visit our{' '}
                  <a
                    className="resarch-tool"
                    target="_blank"
                    href="https://www.troweprice.com/personal-investing/tools/fund-research/"
                    rel="noreferrer"
                  >
                    Mutual Fund Research Tool
                  </a>{' '}
                  to learn more and find the right fund for your investment
                  strategy.
                </div>
              </div>
            </div>
            <div
              className={`new-fund-search-buttons-container-scs ${
                hasValue(selectedFund) ? 'selected' : ''
              }`}
            >
              {hasValue(selectedFund) ? (
                <div className="selected-fund-container">
                  <div className="new-fund-search-chosen-fund">
                    SELECTED FUND
                  </div>
                  <span className="added-fund">
                    {selectedFund.name} ({selectedFund.ticker})
                  </span>
                  <div className="new-fund-search-chosen-agreement">
                    I have read the prospectus for the fund in which I am
                    investing and agree to its terms.{' '}
                    <a
                      target="_blank"
                      href={`https://prospectus-express.broadridge.com/summary.asp?doctype=spro&clientid=trowepll&fundid=${selectedFund.cusip}`}
                      rel="noreferrer"
                    >
                      View Prospectus.
                    </a>
                  </div>
                  <NewFundSearchSelectedFundNotes
                    selectedFund={selectedFund}
                    showProspectus={showProspectus}
                    selectedRegistrationProductType={
                      selectedRegistrationProductType
                    }
                    scsProgram={true}
                  />
                </div>
              ) : (
                <div />
              )}
              <Button
                id="fund-search-back-submit"
                btnStyle={this.getButtonStyle()}
                className="e2e-new-fund-add-button"
                disabled={this.shouldButtonDisable()}
                onClick={this.onAddNewFundClick}
                ref={this.addFundButton}
              >
                Add Fund
              </Button>
            </div>
          </div>
        )}
      </>
    );
  }
}
export default NewFundSearch;
